import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Shield, X, Edit2, Trash2, Save, Lock } from 'lucide-react';

export default function RolesManagementTab({ onChange }) {
  const [roles, setRoles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ id: '', label: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', description: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    const res = await authFetch('/api/roles');
    if (res.ok) setRoles(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (s, type = 'success') => {
    setError(''); setSuccess('');
    if (type === 'success') setSuccess(s); else setError(s);
    setTimeout(() => { setSuccess(''); setError(''); }, 3500);
  };

  const create = async () => {
    if (!form.label.trim()) { flash('Bezeichnung erforderlich', 'error'); return; }
    const res = await authFetch('/api/roles', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: form.id.trim() || undefined,
        label: form.label.trim(),
        description: form.description.trim(),
      }),
    });
    if (res.ok) {
      flash(`Rolle "${form.label}" wurde angelegt`);
      setForm({ id: '', label: '', description: '' });
      setShowNew(false);
      await load();
      if (onChange) onChange();
    } else {
      const d = await res.json().catch(() => ({}));
      flash(d.detail || 'Fehler beim Anlegen', 'error');
    }
  };

  const saveEdit = async (id) => {
    const res = await authFetch(`/api/roles/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        label: editForm.label,
        description: editForm.description,
      }),
    });
    if (res.ok) {
      flash('Rolle gespeichert');
      setEditingId(null);
      await load();
      if (onChange) onChange();
    } else {
      const d = await res.json().catch(() => ({}));
      flash(d.detail || 'Fehler', 'error');
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Rolle "${r.label}" wirklich löschen?`)) return;
    const res = await authFetch(`/api/roles/${r.id}`, { method: 'DELETE' });
    if (res.ok) {
      flash(`Rolle "${r.label}" gelöscht`);
      await load();
      if (onChange) onChange();
    } else {
      const d = await res.json().catch(() => ({}));
      flash(d.detail || 'Fehler beim Löschen', 'error');
    }
  };

  return (
    <div data-testid="roles-mgmt-tab">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#9ec043]" /> Rollen verwalten
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            System-Rollen sind gesperrt — können in Bezeichnung & Beschreibung editiert,
            aber nicht gelöscht werden. Eigene Rollen können beliebig angelegt werden.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-role">
          <Plus className="w-4 h-4 mr-1" /> Neue Rolle
        </Button>
      </div>

      {success && <div className="text-sm text-green-600 bg-green-50 rounded p-3 mb-3 border border-green-200" data-testid="roles-success">{success}</div>}
      {error && <div className="text-sm text-red-600 bg-red-50 rounded p-3 mb-3 border border-red-200" data-testid="roles-error">{error}</div>}

      {showNew && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm" data-testid="new-role-form">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-[#32373c]">Neue Rolle anlegen</h3>
            <button onClick={() => setShowNew(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs text-gray-500">Bezeichnung*</Label>
              <Input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="z.B. Buchhaltung" className="mt-1 bg-white border-gray-300 text-sm" data-testid="new-role-label" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">ID (optional — wird automatisch aus Bezeichnung generiert)</Label>
              <Input value={form.id} onChange={e => setForm(p => ({ ...p, id: e.target.value }))} placeholder="z.B. buchhaltung" className="mt-1 bg-white border-gray-300 text-sm font-mono" data-testid="new-role-id" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-gray-500">Beschreibung</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="z.B. Rechnungswesen, Steuer, Zahlungen" className="mt-1 bg-white border-gray-300 text-sm" data-testid="new-role-description" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={create} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-create-role">Anlegen</Button>
            <Button variant="outline" onClick={() => setShowNew(false)} className="text-sm">Abbrechen</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {roles.map(r => (
          <div key={r.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`role-row-${r.id}`}>
            {editingId === r.id ? (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Bezeichnung</Label>
                    <Input value={editForm.label} onChange={e => setEditForm(p => ({ ...p, label: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" data-testid={`edit-role-label-${r.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">ID</Label>
                    <Input value={r.id} disabled className="mt-1 bg-gray-50 border-gray-300 text-sm font-mono text-gray-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-gray-500">Beschreibung</Label>
                    <Input value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" data-testid={`edit-role-description-${r.id}`} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => saveEdit(r.id)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid={`btn-save-role-${r.id}`}>
                    <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                  </Button>
                  <Button variant="outline" onClick={() => setEditingId(null)} className="text-sm">Abbrechen</Button>
                </div>
              </div>
            ) : (
              <div className="p-3 flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-[#9ec043]/10 flex items-center justify-center text-[#9ec043] shrink-0">
                  {r.system ? <Lock className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#32373c] flex items-center gap-2">
                    {r.label}
                    {r.system && <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">System</span>}
                  </div>
                  <div className="text-xs text-gray-400">
                    <span className="font-mono">{r.id}</span>
                    {r.description ? ` · ${r.description}` : ''}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => { setEditingId(r.id); setEditForm({ label: r.label, description: r.description || '' }); }}
                    className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded"
                    data-testid={`btn-edit-role-${r.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  {!r.system && (
                    <button
                      onClick={() => remove(r)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                      data-testid={`btn-delete-role-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
